import { cn } from '@quadralize/utils'
import { AltArrowLeft, AltArrowRight } from '@solar-icons/react'
import { ptBR } from 'date-fns/locale'
import type * as React from 'react'
import { DayPicker, type DropdownProps } from 'react-day-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../forms/select/select'

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  minYear?: number
  maxYear?: number
}

const JANUARY = 0
const DECEMBER = 11

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout,
  minYear,
  maxYear,
  ...props
}: CalendarProps) {
  const startMonth = minYear ? new Date(minYear, JANUARY) : undefined
  const endMonth = maxYear ? new Date(maxYear, DECEMBER) : undefined

  return (
    <DayPicker
      locale={ptBR}
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      className={cn('p-3', className)}
      classNames={{
        /* ROOT & FIXED SIZE */
        root: 'rdp-root bg-bg-elevated shadow-lgrounded-lg w-full relative',
        months: 'flex flex-col md:flex-row gap-8 justify-center items-center md:items-start',
        // Responsive width prevents horizontal scrolls on small screens
        month: 'rounded-lg flex-1 w-full md:min-w-[300px] flex flex-col relative',

        /* 1. DECORATED NAVIGATION */
        month_caption: 'flex justify-center h-12 mb-4 relative items-center',
        // Fix: pointer-events-none lets clicks "fall through" to the dropdowns below
        nav: 'absolute top-[1.25rem] left-[1rem] right-[1rem] flex justify-between  px-2 z-10 pointer-events-none',
        button_previous: `
            pointer-events-auto flex items-center justify-center 
            w-8 h-8 rounded-full bg-bg-muted text-text-secondary 
            hover:bg-primary hover:text-primary-fg 
            hover:shadow-[0_0_15px_rgba(var(--color-primary),0.4)] 
            transition-all duration-300 cursor-pointer
          `,
        button_next: `
            pointer-events-auto flex items-center justify-center 
            w-8 h-8 rounded-full bg-bg-muted text-text-secondary 
            hover:bg-primary hover:text-primary-fg 
            hover:shadow-[0_0_15px_rgba(var(--color-primary),0.4)] 
            transition-all duration-300 cursor-pointer
          `,
        chevron: 'fill-current w-5 h-5',

        /* 2 & 4. CENTERED & WORKING DROPDOWNS */
        caption_label: captionLayout ? 'hidden' : '',
        dropdowns: 'flex gap-2 justify-center items-center relative z-20',
        dropdown_root: 'flex items-center',
        dropdown: 'hidden', // Hide default dropdown since we use custom component

        /* WEEKDAYS & DAYS */
        month_grid: 'mx-auto',
        weekday: 'text-text-tertiary font-medium text-sm pb-2',
        day: 'text-text-secondary hover:bg-primary/10 transition-colors rounded',
        day_button: 'w-10 h-10 flex items-center justify-center',

        outside: 'text-text-tertiary opacity-50 cursor-not-allowed pointer-events-none',

        /* SELECTED STATES */
        today: 'text-primary font-bold',
        selected: 'bg-primary text-primary-fg',
        range_start: 'bg-primary text-primary-fg rounded-l-md rounded-r-none',
        range_middle: 'bg-primary/20 text-text-primary rounded-none',
        range_end: 'bg-primary text-primary-fg rounded-r-md rounded-l-none',
        disabled: 'text-text-tertiary opacity-50 cursor-not-allowed pointer-events-none',
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === 'left' ? AltArrowLeft : AltArrowRight
          return <Icon weight="Bold" className="h-4 w-4" />
        },
        CaptionLabel({ children }) {
          return <div className="font-bold">{children}</div>
        },
        Dropdown: ({ value, onChange, options }: DropdownProps) => {
          const selected = options?.find((option) => option.value === value)
          const handleChange = (val: string) => {
            onChange?.({ target: { value: val } } as React.ChangeEvent<HTMLSelectElement>)
          }

          return (
            <Select value={value?.toString()} onValueChange={handleChange}>
              <SelectTrigger className="h-8 min-w-[80px] text-xs bg-transparent border-border-subtle hover:bg-bg-subtle transition-colors">
                <SelectValue placeholder={selected?.label} />
              </SelectTrigger>
              <SelectContent>
                {options?.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        },
      }}
      startMonth={startMonth}
      endMonth={endMonth}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
